import { getApiBaseUrl, getToken } from "./utils";

const API_URL = `${getApiBaseUrl()}/templates`;

async function buildApiError(response, fallbackMessage) {
  try {
    const payload = await response.json();
    const detail = payload?.detail ?? payload?.message ?? payload;
    return new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  } catch (_error) {
    return new Error(fallbackMessage);
  }
}

function authHeaders() {
  return {
    Authorization: `Bearer ${getToken()}`,
    "Content-Type": "application/json",
  };
}

function buildQuery(params = {}) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value);
    }
  });

  const queryString = query.toString();
  return queryString ? `?${queryString}` : "";
}

export async function getAssetLibraryItems(params = {}) {
  const response = await fetch(`${API_URL}/asset-library${buildQuery(params)}`, {
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  });

  if (!response.ok) throw await buildApiError(response, "Failed to load asset library");
  return await response.json();
}

export async function getAssetLibraryItem(assetId) {
  const response = await fetch(`${API_URL}/asset-library/${assetId}`, {
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  });

  if (!response.ok) throw await buildApiError(response, "Failed to load asset library item");
  return await response.json();
}

export async function createAssetLibraryItem(asset) {
  const response = await fetch(`${API_URL}/asset-library`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(asset),
  });

  if (!response.ok) throw await buildApiError(response, "Failed to create asset library item");
  return await response.json();
}

export async function uploadAssetLibraryItem({ file, ...asset }) {
  const formData = new FormData();
  formData.append("file", file);

  Object.entries(asset).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    formData.append(key, Array.isArray(value) ? value.join(", ") : value);
  });

  const response = await fetch(`${API_URL}/asset-library/upload`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
    body: formData,
  });

  if (!response.ok) throw await buildApiError(response, "Failed to upload asset");
  return await response.json();
}

export async function updateAssetLibraryItem(assetId, asset) {
  const response = await fetch(`${API_URL}/asset-library/${assetId}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(asset),
  });

  if (!response.ok) throw await buildApiError(response, "Failed to update asset library item");
  return await response.json();
}

export async function deleteAssetLibraryItem(assetId) {
  const response = await fetch(`${API_URL}/asset-library/${assetId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  });

  if (!response.ok) throw await buildApiError(response, "Failed to delete asset library item");
  return await response.json();
}

export async function generateAssetLibraryItem(assetId) {
  const response = await fetch(`${API_URL}/asset-library/${assetId}/generate`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  });

  if (!response.ok) throw await buildApiError(response, "Failed to generate asset");
  return await response.json();
}

export async function generateAssetLibraryVariant(assetId) {
  const response = await fetch(`${API_URL}/asset-library/${assetId}/generate-variant`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  });

  if (!response.ok) throw await buildApiError(response, "Failed to generate asset variant");
  return await response.json();
}

export async function cleanAssetLibraryCheckerboard(assetId, mode = "safe") {
  const query = new URLSearchParams({ mode }).toString();
  const response = await fetch(`${API_URL}/asset-library/${assetId}/clean-checkerboard?${query}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  });

  if (!response.ok) throw await buildApiError(response, "Failed to clean checkerboard background");
  return await response.json();
}

export async function removeAssetLibrarySolidBackground(assetId, mode = "safe") {
  const query = new URLSearchParams({ mode }).toString();
  const response = await fetch(`${API_URL}/asset-library/${assetId}/remove-solid-background?${query}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
  });

  if (!response.ok) throw await buildApiError(response, "Failed to remove solid background");
  return await response.json();
}
